import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout, Typography, Input, Button, Tooltip, Avatar, Empty,
  Spin, Alert, Popconfirm, message, Tag
} from 'antd';
import {
  UserOutlined, LogoutOutlined, LockOutlined, UnlockOutlined,
  SaveOutlined, PlusOutlined, DeleteOutlined, SearchOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import EnvVersionLabel from './Envversionlabel';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

// public/rules/chronic_conditions.json is served at /rules/chronic_conditions.json
const CHRONIC_CONDITIONS_URL = '/rules/chronic_conditions.json';

const EDITABLE_ROLES = ['hcc_reviewer'];


/**
 * ChronicConditionsConfig
 *
 * Flat-list editor for the chronic conditions vocabulary.
 *
 * Shell (Sider + Header + gradient Content) mirrors SectionDefinitionsEditor
 * and WorkQueuePage. Breadcrumb is "HCC STUDIO / Chronic
 * Conditions", back-link returns to /hcc-config. Lock and Save buttons sit
 * top-right; Save is a placeholder (no persistence wired up).
 *
 * Body: a single card. Top row is one input that does both search and
 * new-entry duty, with an Add button. As the user types:
 *   - The list below filters by case-insensitive substring match.
 *   - If the trimmed input exactly matches an existing condition
 *     (case-insensitive), an inline indicator names the existing entry, the
 *     matching row in the list gets a subtle highlight, and the Add button
 *     is disabled.
 *
 * Edit affordances (delete row, Add button enable) are gated by editMode.
 * Searching works regardless of lock state.
 *
 * Data:
 *   - Fetched from /rules/chronic_conditions.json (public/rules).
 *   - Source-of-truth ordering is preserved; existing literal duplicates in
 *     the file are NOT auto-removed — they show up as separate rows so the
 *     user can see and clean them up in edit mode.
 *   - All edits are in-memory. No backend.
 */
const ChronicConditionsConfig = ({
  username,
  sessionId,
  userRole,
  userFeatures,
  tabOrder,
  onLogout,
}) => {
  const navigate = useNavigate();

  // ── State ────────────────────────────────────────────────────────────
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [conditions, setConditions] = useState([]);  // string[]
  const [inputText, setInputText] = useState('');
  const [editMode, setEditMode]   = useState(false); // false = locked

  const canEdit = EDITABLE_ROLES.includes(userRole);


  // ── Load chronic_conditions.json ─────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    fetch(CHRONIC_CONDITIONS_URL)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then(data => {
        if (cancelled) return;
        const arr = Array.isArray(data)
          ? data.map(s => String(s).trim()).filter(Boolean)
          : [];
        setConditions(arr);
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to load chronic_conditions.json:', error);
        setLoadError(error.message || 'Unknown error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // ── Derived ──────────────────────────────────────────────────────────
  const trimmedInput = inputText.trim();
  const inputLower   = trimmedInput.toLowerCase();

  // Pair each condition with its original index so deletion targets the
  // exact row the user clicked, even when duplicates exist.
  const indexedConditions = useMemo(
    () => conditions.map((c, idx) => ({ value: c, originalIdx: idx })),
    [conditions]
  );

  const filteredConditions = useMemo(() => {
    if (!trimmedInput) return indexedConditions;
    return indexedConditions.filter(item =>
      item.value.toLowerCase().includes(inputLower)
    );
  }, [indexedConditions, trimmedInput, inputLower]);

  // The matching condition text, in its original casing, or null.
  const exactMatch = useMemo(() => {
    if (!trimmedInput) return null;
    return conditions.find(c => c.toLowerCase() === inputLower) || null;
  }, [conditions, trimmedInput, inputLower]);

  const canAdd = editMode && trimmedInput.length > 0 && !exactMatch;

  // ── Handlers ─────────────────────────────────────────────────────────
  const handleLockToggle = () => setEditMode(prev => !prev);

  const handleSave = () => {
    message.info('Save is not yet wired up — edits are in memory only.');
  };

  const handleAdd = () => {
    if (!canAdd) return;
    setConditions(prev => [...prev, trimmedInput]);
    setInputText('');
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleDelete = (originalIdx) => {
    setConditions(prev => prev.filter((_, i) => i !== originalIdx));
  };

  // Tooltip text for the Add button explaining its disabled state.
  let addButtonHint = '';
  if (!editMode)              addButtonHint = 'Unlock to add new conditions';
  else if (!trimmedInput)     addButtonHint = 'Type a condition name to add';
  else if (exactMatch)        addButtonHint = 'This condition is already in the list';
  else                        addButtonHint = 'Add this condition';

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <Layout style={{ minHeight: '100vh' }}>

      {/* ── Sider (mirrors SectionDefinitionsEditor / WorkQueuePage) ── */}
      <Sider
        width={60}
        collapsible
        collapsed={true}
        collapsedWidth={60}
        trigger={null}
        className="sidebar-gradient"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 10,
        }}
      >
        <div style={{
          display: 'flex',
          background: 'linear-gradient(180deg, #162349 0%, #2a4a7c 100%)',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
          alignItems: 'center',
          padding: '20px 0',
        }}>
          <div></div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
            <Tooltip title={username} placement="right">
              <Avatar
                style={{
                  backgroundColor: 'white',
                  color: '#162349',
                  cursor: 'pointer',
                  border: '2px solid white',
                }}
                icon={<UserOutlined />}
              />
            </Tooltip>

            <Tooltip title="Logout" placement="right">
              <Button
                type="text"
                icon={<LogoutOutlined style={{ color: 'white' }} />}
                onClick={onLogout}
                style={{ color: 'white' }}
              />
            </Tooltip>

            <EnvVersionLabel style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: 'Bold' }} />
          </div>
        </div>
      </Sider>

      <Layout style={{ marginLeft: 60 }}>

        {/* ── Header ──────────────────────────────────────────────── */}
        <Header style={{
          background: '#162349',
          padding: '0 20px',
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          {/* Breadcrumb */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
            <span
              onClick={() => navigate('/hcc-config')}
              style={{
                color: 'rgba(255,255,255,0.75)',
                fontSize: 20,
                fontWeight: 700,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = 'white')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.75)')}
            >
              HCC STUDIO
            </span>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }}>/</span>
            <span style={{
              color: 'white',
              fontSize: 20,
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              Chronic Conditions
            </span>
          </div>

          {/* Lock + Save */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {canEdit ? (
              <>
                <Tooltip title={editMode ? 'Click to lock (return to view-only)' : 'Click to unlock for editing'}>
                  <Button
                    icon={editMode ? <UnlockOutlined /> : <LockOutlined />}
                    onClick={handleLockToggle}
                    style={{
                      backgroundColor: editMode ? '#FEF3C7' : 'white',
                      color: '#162349',
                      fontWeight: 600,
                      borderColor: editMode ? '#F59E0B' : '#d9d9d9',
                    }}
                  >
                    {editMode ? 'Unlocked' : 'Locked'}
                  </Button>
                </Tooltip>
                <Tooltip title={editMode ? 'Save (not yet wired up)' : 'Unlock to enable saving'}>
                  <Button
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    disabled={!editMode}
                    style={{ backgroundColor: 'white', color: '#162349', fontWeight: 600 }}
                  >
                    Save
                  </Button>
                </Tooltip>
              </>
            ) : (
              <Tag
                style={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  fontWeight: 600,
                  fontSize: 13,
                  padding: '4px 12px',
                }}
              >
                Display Only
              </Tag>
            )}
          </div>

        </Header>

        {/* ── Content ─────────────────────────────────────────────── */}
        <Content style={{
          padding: '28px 32px',
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #ced4da 90%, #ced4da 100%)',
          minHeight: 'calc(100vh - 64px)',
        }}>

          {/* Loading */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16, color: '#6B7280', fontSize: 14 }}>
                Loading chronic conditions…
              </div>
            </div>
          )}

          {/* Load error */}
          {!loading && loadError && (
            <Alert
              type="warning"
              showIcon
              message="Configuration file not found"
              description={
                <span>
                  Could not load <code>{CHRONIC_CONDITIONS_URL}</code>. ({loadError})
                </span>
              }
              style={{ maxWidth: 640, margin: '40px auto' }}
            />
          )}

          {/* Editor */}
          {!loading && !loadError && (
            <div style={{
              background: 'white',
              borderRadius: 8,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              padding: '24px 28px',
              maxWidth: 920,
              margin: '0 auto',
            }}>
              {/* Top-of-card title */}
              <div style={{ marginBottom: 16 }}>
                <Text style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#9CA3AF',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}>
                  Chronic conditions vocabulary
                </Text>
                <Title level={4} style={{ margin: '4px 0 0 0', color: '#111827' }}>
                  {conditions.length} condition{conditions.length === 1 ? '' : 's'} defined
                </Title>
              </div>

              {/* Search / add row */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <Input
                  prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
                  placeholder="Search conditions, or type a new one to add…"
                  value={inputText}
                  onChange={e => setInputText(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                  allowClear
                  size="large"
                />
                <Tooltip title={addButtonHint}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                    disabled={!canAdd}
                    size="large"
                    style={{
                      backgroundColor: canAdd ? '#162349' : undefined,
                      borderColor: canAdd ? '#162349' : undefined,
                      fontWeight: 600,
                      minWidth: 96,
                    }}
                  >
                    Add
                  </Button>
                </Tooltip>
              </div>

              {/* Inline duplicate indicator */}
              {exactMatch && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  marginBottom: 16,
                  background: '#FEF3C7',
                  border: '1px solid #F59E0B',
                  borderRadius: 6,
                  fontSize: 13,
                  color: '#92400E',
                }}>
                  <ExclamationCircleFilled style={{ color: '#F59E0B', fontSize: 14 }} />
                  <span>
                    <strong>“{exactMatch}”</strong> is already in the list.
                  </span>
                </div>
              )}

              {/* Spacer when there is no duplicate banner */}
              {!exactMatch && <div style={{ height: 16 }} />}

              {/* List */}
              <div style={{
                border: '1px solid #E5E7EB',
                borderRadius: 6,
                background: '#FAFAFA',
                maxHeight: 'calc(100vh - 64px - 56px - 200px)',
                minHeight: 320,
                overflowY: 'auto',
              }}>
                {filteredConditions.length === 0 ? (
                  <div style={{ padding: '40px 16px', textAlign: 'center' }}>
                    <Empty
                      description={
                        trimmedInput
                          ? `No conditions match “${trimmedInput}”`
                          : 'No chronic conditions defined.'
                      }
                    />
                  </div>
                ) : (
                  filteredConditions.map(({ value, originalIdx }) => {
                    const isExactDup =
                      exactMatch && value.toLowerCase() === inputLower;
                    return (
                      <div
                        key={originalIdx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 8,
                          padding: '10px 16px',
                          borderBottom: '1px solid #F0F0F0',
                          background: isExactDup ? '#FFFBEB' : 'transparent',
                          transition: 'background 0.12s',
                        }}
                      >
                        <span style={{
                          fontSize: 14,
                          color: '#111827',
                          fontWeight: isExactDup ? 600 : 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                        }}>
                          {value}
                        </span>

                        {editMode && (
                          <Popconfirm
                            title="Delete this condition?"
                            description={`Remove “${value}”?`}
                            onConfirm={() => handleDelete(originalIdx)}
                            okText="Delete"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                          >
                            <Button
                              size="small"
                              type="text"
                              icon={<DeleteOutlined style={{ color: '#DC2626' }} />}
                            />
                          </Popconfirm>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Footer count */}
              <div style={{
                marginTop: 12,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <Text style={{ fontSize: 12, color: '#9CA3AF' }}>
                  {trimmedInput
                    ? `Showing ${filteredConditions.length} of ${conditions.length} conditions`
                    : `${conditions.length} conditions total`}
                </Text>
                {!editMode && (
                  <Text style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>
                    Unlock to edit
                  </Text>
                )}
              </div>
            </div>
          )}

        </Content>
      </Layout>
    </Layout>
  );
};

export default ChronicConditionsConfig;